import React, { useState, useEffect } from 'react';
import styles from "./manufacturers.module.css";
import { getManufacturerThunk } from '../../redux/vendorSlice';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

const Manufacturers = () => {
  const [activeTab, setActiveTab] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [organisedManufacturer, setManufacturers] = useState({});
  const { manufacturers, loading } = useSelector((state) => state.vendor);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(getManufacturerThunk());
  }, [dispatch]);

  useEffect(() => {
    const filtered = (manufacturers || []).filter(item => {
      const hasPhoto = item.thumbnail?.photo && item.thumbnail.photo.length > 0 && item.thumbnail.photo[0];
      return hasPhoto && (item.name || '').trim().toLowerCase().includes(searchQuery.toLowerCase());
    });
    const organizedData = organizeManufacturers(filtered);
    setManufacturers(organizedData);
  }, [manufacturers, searchQuery]);

  const organizeManufacturers = (data) => {
    const organized = {};
    const seen = new Set();

    data.forEach(item => {
      const trimmedName = (item.name || '').trim();
      if (!trimmedName) return;

      const normKey = trimmedName.toLowerCase();
      if (seen.has(normKey)) return;
      seen.add(normKey);

      let firstLetter = trimmedName.charAt(0).toUpperCase();
      if (!isNaN(firstLetter)) {
        firstLetter = '0-9';
      }
      if (!organized[firstLetter]) {
        organized[firstLetter] = [];
      }
      organized[firstLetter].push({ ...item, name: trimmedName });
    });

    Object.keys(organized).forEach(letter => {
      organized[letter].sort((a, b) => a.name.localeCompare(b.name));
    });

    return organized;
  };

  const handleRedirect = (letter) => {
    setActiveTab(letter);
    if (letter === 'All') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      const targetElement = document.getElementById(`letter-${letter}`);
      if (targetElement) {
        const yOffset = -140; 
        const y = targetElement.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }
  };

  const handleClick = (name) => {
    const data = {
      source: 'manufacturer',
      manufacturer: name,
      selectedAttributes: [{
        attribute: "Manufacturer",
        values: [name]
      }]
    };
    navigate('/SimilarProductList', { state: data });
  };

  const formatImageUrl = (url) => {
    if (!url) return '';
    if (url.includes('asset.electrowiz.in')) {
      return url.replace('https://asset.electrowiz.in', 'https://electrowiz-ap.s3.ap-south-1.amazonaws.com');
    }
    return url;
  };

  const availableLetters = ['All', ...Object.keys(organisedManufacturer).sort()];

  return (
    <div className={styles.pageWrapper}>
      {/* Clean Sitewide Header */}
      <div className={styles.imgBread}>
        <div className={styles.breadContent}>
          <span className={styles.pageTitle}>Manufacturers</span>
        </div>
      </div>

      {/* Main Content Body */}
      <div className={styles.mainContainer}>
        {/* Search Bar & Alphabet Filter */}
        <div className={styles.filterControlsContainer}>
          <div className={styles.searchBoxWrapper}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/>
              <line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              type="text"
              placeholder="Search manufacturers (e.g. 3M, Yageo, Texas Instruments)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={styles.searchInput}
            />
            {searchQuery && (
              <button className={styles.clearSearchBtn} onClick={() => setSearchQuery('')}>✕</button>
            )}
          </div>

          <div className={styles.alphabetNav}>
            {availableLetters.map((letter) => (
              <button
                key={letter}
                onClick={() => handleRedirect(letter)}
                className={`${styles.alphabetPill} ${activeTab === letter ? styles.activePill : ''}`}
              >
                {letter}
              </button>
            ))}
          </div>
        </div>

        {/* Directory Card Sections */}
        {loading ? (
          <div className={styles.loadingContainer}>
            <div className={styles.spinner}></div>
            <p>Loading manufacturer directory...</p>
          </div>
        ) : Object.keys(organisedManufacturer).length === 0 ? (
          <div className={styles.emptyState}>
            <h3>No manufacturers match "{searchQuery}"</h3>
            <p>Try searching with another manufacturer keyword or reset your filter.</p>
            <button className={styles.resetBtn} onClick={() => setSearchQuery('')}>Reset Search</button>
          </div>
        ) : (
          <div className={styles.sectionsContainer}>
            {Object.keys(organisedManufacturer).sort().map((letter) => {
              const list = organisedManufacturer[letter];
              return (
                <div key={letter} id={`letter-${letter}`} className={styles.letterSection}>
                  <div className={styles.letterHeaderRow}>
                    <div className={styles.letterBadge}>{letter}</div>
                    <span className={styles.letterCount}>{list.length} brand{list.length !== 1 ? 's' : ''}</span>
                    <div className={styles.letterLine}></div>
                  </div>

                  <div className={styles.cardsGrid}>
                    {list.map((m) => (
                      <div
                        key={m._id || m.name}
                        className={styles.manufacturerCard}
                        onClick={() => handleClick(m.name)}
                      >
                        <div className={styles.logoWrapper}>
                          {m.thumbnail?.photo?.[0] ? (
                            <img
                              src={formatImageUrl(m.thumbnail.photo[0])}
                              alt={m.name}
                              className={styles.manufacturerLogo}
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div
                            className={styles.placeholderLogo}
                            style={{ display: m.thumbnail?.photo?.[0] ? 'none' : 'flex' }}
                          >
                            {m.name.charAt(0).toUpperCase()}
                          </div>
                        </div>

                        <div className={styles.cardInfo}>
                          <h3 className={styles.manufacturerTitle}>{m.name}</h3>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Manufacturers;
